import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useLogout, useMe } from "../hooks/useAuth";
import { PageTransition, easeOut, fadeUp, layoutSpring, panel } from "../motion";
import { useSettings } from "../hooks/useSettings";
import { applyTheme } from "../theme";
import { DailyLogPage } from "../pages/DailyLogPage";
import { DashboardPage } from "../pages/DashboardPage";
import { ResidentsPage } from "../pages/ResidentsPage";
import { ForecastPage } from "../pages/ForecastPage";
import { RotaPage } from "../pages/RotaPage";
import { SettingsPage } from "../pages/SettingsPage";
import { TodosPage } from "../pages/TodosPage";
import { CommandPalette } from "./CommandPalette";
import { ThemeControls } from "./ThemeControls";
import { IconDashboard, IconLogout, IconPanelLeft, IconPanelRight, IconResidents, IconRota, IconSearch, IconSettings, IconTodos } from "./icons";

const NAV_MAIN = [
  { to: "/", label: "Dashboard", icon: <IconDashboard />, end: true },
] as const;

const NAV_WORK = [
  { to: "/todos", label: "To-dos", icon: <IconTodos />, end: false },
  { to: "/rota", label: "Rota", icon: <IconRota />, end: false },
  { to: "/residents", label: "Residents", icon: <IconResidents />, end: false },
] as const;

const NAV_DESKTOP_W = 200;
const NAV_TABLET_W = 68;

const SIDEBAR_KEY = "otdesk.sidebar-collapsed";

function initialCollapsed(): boolean {
  const stored = localStorage.getItem(SIDEBAR_KEY);
  if (stored === "1") return true;
  if (stored === "0") return false;
  return typeof window !== "undefined" && window.innerWidth < 1024;
}

type NavEntry = { to: string; label: string; icon: ReactNode; end: boolean };

/**
 * Stable identity for the page-level transition. The day-note page is served
 * at both "/" (today) and "/log/:date?" (specific days), so the date param
 * must not remount/re-animate the page — otherwise every prev/next-day click
 * fades the whole page out and back in (a flash). Other routes keep their
 * exit/entrance transition when switching between pages.
 */
function pageKey(pathname: string): string {
  const seg = pathname.split("/")[1] ?? "";
  // The day-note page is served at both "/" (today) and "/log/:date?" — merge
  // them into one identity so prev/next-day navigation swaps content in place.
  // Every other route keeps its exact pathname identity (unchanged behaviour).
  return seg === "" || seg === "log" ? "daynote" : pathname;
}

function NavLabel({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {!collapsed ? (
        <motion.span
          key="lbl"
          className="nav-label"
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "auto" }}
          exit={{ opacity: 0, width: 0 }}
          transition={{ duration: 0.22, ease: easeOut }}
        >
          {children}
        </motion.span>
      ) : null}
    </AnimatePresence>
  );
}

export function AppShell() {
  const { settings } = useSettings();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe().data;
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (settings) applyTheme(settings.theme, settings.accent);
  }, [settings?.theme, settings?.accent, settings]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;
    function onDocDown(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [userMenuOpen]);

  async function onLogout() {
    try {
      await logout.mutateAsync();
    } catch {
      // Double-click or an already-expired session: the request 401s, but the
      // cache clear (useLogout onSettled) and the navigation below still
      // complete the logout — nothing to recover here.
    }
    navigate("/login", { replace: true });
  }

  function openSearch() {
    setPaletteOpen(true);
  }

  function navLink(item: NavEntry) {
    return (
      <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
        {({ isActive }) => (
          <>
            {isActive && <motion.span layoutId="sidebar-active-bg" className="nav-active-bg" transition={layoutSpring} />}
            {isActive && <motion.span layoutId="sidebar-active-bar" className="nav-active-bar" transition={layoutSpring} />}
            <span className="nav-content">
              {item.icon}
              <NavLabel collapsed={collapsed}>{item.label}</NavLabel>
            </span>
          </>
        )}
      </NavLink>
    );
  }
  return (
    <div className={`shell ${collapsed ? "collapsed" : ""}`}>
      <motion.header className="topbar" variants={fadeUp} initial="hidden" animate="show">
        <span className="topbar-brand">
          <img className="brand-monogram" src="/icon-192.png" alt="" />
          <span className="brand-name">OT Desk</span>
        </span>
        <div className="topbar-search-wrap">
          <IconSearch />
          <input className="topbar-search" type="search" placeholder="Search…" aria-label="Search (Ctrl+K)" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPaletteOpen(true); }} onFocus={openSearch} />
        </div>
        <ThemeControls />
        <div className="dropdown" ref={userMenuRef}>
          <motion.button
            type="button"
            className="user-trigger"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen((o) => !o)}
            whileTap={{ scale: 0.97 }}
          >
            <img className="avatar" src="/avatar.png" alt="" />
            <span className="user-name">{me?.user.username}</span>

          </motion.button>
          <AnimatePresence>
            {userMenuOpen ? (
              <motion.div className="dropdown-menu" role="menu" variants={panel} initial="hidden" animate="show" exit="exit" style={{ right: 0, left: "auto" }}>
                <button type="button" className="dropdown-item" role="menuitem" onClick={() => { setUserMenuOpen(false); navigate("/settings"); }}>
                  <IconSettings />
                  <span>Settings</span>
                </button>
                <div className="dropdown-divider" />
                <button type="button" className="dropdown-item danger" role="menuitem" onClick={() => { setUserMenuOpen(false); onLogout(); }}>
                  <IconLogout />
                  <span>Log out</span>
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.header>
      <div className="shell-body">
        <motion.nav
          className={`sidebar ${collapsed ? "collapsed" : ""}`}
          aria-label="Main"
          initial={{ opacity: 0, y: 12, width: collapsed ? NAV_TABLET_W : NAV_DESKTOP_W }}
          animate={{ opacity: 1, y: 0, width: collapsed ? NAV_TABLET_W : NAV_DESKTOP_W }}
          transition={{ width: { duration: 0.2, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.34, ease: easeOut, delay: 0.05 }, y: { duration: 0.34, ease: easeOut, delay: 0.05 } }}
        >
          <LayoutGroup id="sidebar-nav">
            <div className="sidebar-scroll">
              {NAV_MAIN.map(navLink)}
              {NAV_WORK.map(navLink)}
            </div>
            <div className="sidebar-foot">
              <motion.button type="button" className="nav-item nav-toggle" onClick={() => setCollapsed((c) => !c)} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} whileTap={{ scale: 0.97 }}>
                {collapsed ? <IconPanelRight /> : <IconPanelLeft />}
                <NavLabel collapsed={collapsed}>{collapsed ? "Expand" : "Collapse"}</NavLabel>
              </motion.button>
            </div>
          </LayoutGroup>
        </motion.nav>
        <div className="app-main">
          <main className="content">
            {/* Freeze the exiting page router context during exit: the location prop is
                baked into the keyed PageTransition wrapper, so the old route pathname,
                search params, and route params stay resolved while it fades out. A plain
                <Outlet /> would re-resolve against the new route mid-exit, causing the
                "destination → blank → destination" flash. mode="popLayout" mounts the
                next page immediately (underneath) so there is never a blank gap between
                pages — the exiting page crossfades out on top. */}
            <AnimatePresence mode="popLayout">
              <PageTransition key={pageKey(location.pathname)} className="page">
                <Routes location={location}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/todos" element={<TodosPage />} />
                  <Route path="/log/:date?" element={<DailyLogPage />} />
                  <Route path="/rota" element={<RotaPage />} />
                  <Route path="/residents" element={<ResidentsPage />} />
                  <Route path="/residents/:id" element={<ResidentsPage />} />
                  <Route path="/reminders" element={<Navigate to="/todos" replace />} />
                  <Route path="/forecast" element={<ForecastPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PageTransition>
            </AnimatePresence>
          </main>
          <nav className="tabbar" aria-label="Main">
            {[...NAV_MAIN, ...NAV_WORK].map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => (isActive ? "active" : "")}>
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <CommandPalette open={paletteOpen} query={searchQuery} onQueryChange={setSearchQuery} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

export function ShellPage({ children }: { children: ReactNode }) {
  return <div className="page-head">{children}</div>;
}
